/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SyncTestCaseDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import integrationConfigAction from "../../../redux/actions/integrationConfigAction";
import testCasesActions from "../../../redux/actions/testCasesActions";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CommonStepper from "../../../components/customComponentsV2/common/Stepper/CommonStepper";
import { componentStyle } from "../../../config/appTheme";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";

const SYNC_STEPS = [
  "Select Integration",
  "Import Test Cases",
  "Review and Complete",
  // "Complete",
];

const IMPORT_TYPES = {
  SINGLE: "Single",
  MULTIPLE: "Multiple",
};

function SyncTestCaseDialog({ open, onClose, selectedTestCases = [] }) {
  const dispatch = useDispatch();
  const selectedTestCasesCount = selectedTestCases.length;

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [importType, setImportType] = useState(IMPORT_TYPES.SINGLE);
  const [testKey, setTestKey] = useState("");
  const [projectKey, setProjectKey] = useState("");
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch available Xray integrations
  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await dispatch(
        HandleApiActions({
          ...integrationConfigAction.view_integrations,
          params: {
            is_connected: true,
            is_deleted: false,
            module_type: "TEST CASES",
          },
        }),
      );
      if (!resp?.error && resp?.data) {
        // Filter for Xray integrations
        const xrayIntegrations = resp.data.filter(
          (integration) =>
            integration.connector_type?.toLowerCase().includes("xray") ||
            integration.connector_type?.toLowerCase().includes("jira"),
        );
        setIntegrations(xrayIntegrations);
      }
    } catch (error) {
      setErrorMessage(error?.message || "Failed to fetch integrations");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setImportStatus("idle");
      setImportProgress(0);
      setErrorMessage("");
      setImportType(IMPORT_TYPES.SINGLE);
      setTestKey("");
      setProjectKey("");
      fetchIntegrations();
    }
  }, [open, fetchIntegrations]);

  const validateImportInput = () => {
    if (!selectedIntegration || !importType) {
      return "Please select an integration and import type";
    }

    if (importType === IMPORT_TYPES.SINGLE && !testKey.trim()) {
      return "Please enter a test key";
    }

    if (importType === IMPORT_TYPES.MULTIPLE && !projectKey.trim()) {
      return "Please enter a project key";
    }

    return "";
  };

  const getImportRequest = () => {
    const baseRequest = {
      id: Number(selectedIntegration),
      module_type: "TEST CASES",
      type: "xray",
    };

    if (importType === IMPORT_TYPES.SINGLE) {
      return {
        apiAction: integrationConfigAction.import_single_test_case,
        importData: {
          ...baseRequest,
          action: "getTestCase",
          payload: { testKey: testKey.trim() },
        },
      };
    }

    return {
      apiAction: integrationConfigAction.import_multiple_test_cases,
      importData: {
        ...baseRequest,
        action: "getTestCases",
        payload: { projectKey: projectKey.trim() },
      },
    };
  };

  const hasImportData = (resp) =>
    Boolean(resp?.results?.length || resp?.data?.length);

  // Handle import operation
  const handleImport = async () => {
    const validationError = validateImportInput();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setImportStatus("importing");
    setImportProgress(0);
    setActiveStep(2);

    try {
      const { apiAction, importData } = getImportRequest();

      const importResp = await dispatch(
        HandleApiActions({
          ...apiAction,
          params: importData,
          show_toast: true,
        }),
      );
      if (!hasImportData(importResp)) {
        setImportStatus("error");
        setErrorMessage(importResp?.msg || "Import operation failed");
        return;
      }

      dispatch(HandleApiActions({ ...testCasesActions.get_test_cases }));
      const successCount = importResp?.data?.successful_count || 1;
      setImportStatus(successCount >= 1 ? "success" : "partial");
      setImportProgress(100);
    } catch (error) {
      setImportStatus("error");
      setErrorMessage(error?.message || "Import operation failed");
    }
  };

  // Handle step navigation
  const handleNext = () => {
    // When moving into the review/import step, always start fresh
    if (activeStep === 1) {
      setImportStatus("idle");
      setImportProgress(0);
      setErrorMessage("");
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    // If coming back from the review/import step, clear previous import state
    if (activeStep === 2) {
      setImportStatus("idle");
      setImportProgress(0);
      setErrorMessage("");
    }
    setActiveStep((prev) => prev - 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {loading ? (
              <LinearProgress
                sx={{
                  backgroundColor: "#e8f5e9",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#4caf50" },
                }}
              />
            ) : (
              <CustomSelectV2
                label="Choose the integration to import test cases"
                required={true}
                optionsList={integrations?.map((integration) => ({
                  label: integration.connector_type,
                  value: integration.id,
                }))}
                selectedValue={selectedIntegration || " "}
                handleOnChange={(e) => setSelectedIntegration(e.target.value)}
              />
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ ml: "15px" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  color: "#6f7174",
                  ...componentStyle.font.form.textFieldLabel,
                  fontWeight: 500,
                  textAlign: "left",
                }}
              >
                Type<span style={{ color: "red" }}> *</span>
              </Typography>
            </Box>
            <Box sx={{ ml: "40px" }}>
              <CustomRadioButtonV2
                value={importType}
                onChange={(e) => setImportType(e.target.value)}
                data={[
                  { value: IMPORT_TYPES.SINGLE, label: "Single" },
                  { value: IMPORT_TYPES.MULTIPLE, label: "Multiple" },
                ]}
                displayDirection="row"
              />
            </Box>

            {importType === IMPORT_TYPES.SINGLE && (
              <Box sx={{ mt: 2, ml: 1.7 }}>
                <CustomFormTextFieldV2
                  label="Test Key"
                  required={true}
                  value={testKey}
                  setValue={setTestKey}
                />
              </Box>
            )}

            {importType === IMPORT_TYPES.MULTIPLE && (
              <Box sx={{ mt: 2, ml: 1.7 }}>
                <CustomFormTextFieldV2
                  label="Project Key"
                  required={true}
                  value={projectKey}
                  setValue={setProjectKey}
                />
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            {/* Review Info */}
            <Typography variant="subtitle2" gutterBottom>
              <strong>Review & Confirm Import Configuration</strong>
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Integration:</strong>{" "}
                {integrations.find((i) => i.id === selectedIntegration)
                  ?.connector_type || "Unknown"}
              </Typography>
              <Typography variant="body2">
                <strong>Import Type:</strong> {importType} Test Case
              </Typography>
              <Typography variant="body2">
                <strong>Selected Count:</strong> {selectedTestCasesCount}
              </Typography>
              <Typography variant="body2">
                <strong>Target:</strong>{" "}
                {importType === IMPORT_TYPES.SINGLE
                  ? `Test Key: ${testKey}`
                  : `Project Key: ${projectKey}`}
              </Typography>
            </Box>

            {/* Import Progress */}
            {importStatus === "importing" && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Uploading
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={importProgress}
                  sx={{
                    backgroundColor: "#e8f5e9",
                    "& .MuiLinearProgress-bar": { backgroundColor: "#4caf50" },
                  }}
                />
              </Box>
            )}

            {/* Upload Status */}
            {importStatus === "success" && (
              <Box
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  p: 2,
                  mb: 2,
                  borderRadius: 1,
                }}
              >
                {importType === IMPORT_TYPES.SINGLE ? (
                  <Typography variant="body2">
                    <strong>UPLOADED!</strong> This will import test case "
                    {testKey}"
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <strong>UPLOADED!</strong> This will import all test cases
                    from project "{projectKey}"
                  </Typography>
                )}
              </Box>
            )}

            {importStatus === "error" && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage || "Import operation failed"}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return selectedIntegration !== "";
      case 1:
        if (!importType) return false;
        if (importType === IMPORT_TYPES.SINGLE) {
          return testKey.trim() !== "";
        }
        if (importType === IMPORT_TYPES.MULTIPLE) {
          return projectKey.trim() !== "";
        }
        return true;
      case 2:
        return true; // Review step is always valid if we reach it
      default:
        return true;
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            Sync Test Case
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => onClose(false)}
            sx={{
              color: "inherit",
              padding: "8px",
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <CommonStepper steps={SYNC_STEPS} activeStep={activeStep} />

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Step 0: Select Integration */}
        {activeStep === 0 && (
          <>
            <Box />
            <Box sx={{ display: "flex", gap: 1 }}>
              <CustomButtonV2
                type="cancel"
                btnName="Cancel"
                handleOnClick={() => onClose(false)}
              />
              <CustomButtonV2
                type="submit"
                btnName="Next"
                handleOnClick={handleNext}
                disabled={!isStepValid() || loading}
              />
            </Box>
          </>
        )}

        {/* Step 1: Import Test Cases */}
        {activeStep === 1 && (
          <>
            <CustomButtonV2
              type="submit"
              btnName="Back"
              handleOnClick={handleBack}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <CustomButtonV2
                type="cancel"
                btnName="Cancel"
                handleOnClick={() => onClose(false)}
              />
              <CustomButtonV2
                type="submit"
                btnName="Next"
                handleOnClick={handleNext}
                disabled={!isStepValid() || loading}
              />
            </Box>
          </>
        )}

        {/* Step 2: Review and Complete - Before Import */}
        {activeStep === 2 && importStatus === "idle" && (
          <>
            <CustomButtonV2
              type="submit"
              btnName="Back"
              handleOnClick={handleBack}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <CustomButtonV2
                type="cancel"
                btnName="Cancel"
                handleOnClick={() => onClose(false)}
              />
              <CustomButtonV2
                type="submit"
                btnName="Import"
                handleOnClick={handleImport}
                disabled={loading}
              />
            </Box>
          </>
        )}

        {/* Step 2: Review and Complete - After Import (Success or Error) */}
        {activeStep === 2 &&
          importStatus !== "idle" &&
          importStatus !== "importing" && (
            <>
              <CustomButtonV2
                type="submit"
                btnName="Back"
                handleOnClick={handleBack}
              />
              <CustomButtonV2
                type="submit"
                btnName="Close"
                handleOnClick={() => onClose(false)}
              />
            </>
          )}
      </DialogActions>
    </Dialog>
  );
}

SyncTestCaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedTestCases: PropTypes.array,
};

export default SyncTestCaseDialog;
